import {fromSeconds} from 'common/date';
import * as React from 'react';
import {Modal} from 'src/Modal';
import type {PayrollRecordsQuery} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type PayrollRecordModalUserProps = {
  record: PayrollRecordsQuery['payrollRecords'][number];
};

type Props = PayrollRecordModalUserProps & SystemProps;

/** Record amounts are integer cents. */
const cents = (value: number): string =>
  (value / 100).toLocaleString('en-US', {style: 'currency', currency: 'USD'});

/** Line amounts are dollars as printed on the slip (pipeline convention). */
const dollars = (value: number): string =>
  value.toLocaleString('en-US', {style: 'currency', currency: 'USD'});

/**
 * One slip line as the payslip pipeline stores it — dollars as printed.
 * `lines` arrives as opaque JSON, so rows are narrowed structurally and
 * anything unrecognized is simply not rendered (the record's summary
 * numbers above the table never depend on the lines).
 */
type SlipLine = {
  section: string;
  description: string;
  amount: number;
  ytd?: number;
};

const isSlipLine = (value: unknown): value is SlipLine => {
  if (typeof value !== 'object' || value == null) {
    return false;
  }
  const record: Record<string, unknown> = {...value};
  return (
    typeof record.section === 'string' &&
    typeof record.description === 'string' &&
    typeof record.amount === 'number' &&
    (record.ytd === undefined || typeof record.ytd === 'number')
  );
};

const toSlipLines = (raw: unknown): SlipLine[] =>
  Array.isArray(raw) ? raw.filter(isSlipLine) : [];

// Section headings in the slip's own vocabulary, in print order.
const SECTION_LABELS: Record<string, string> = {
  earnings: 'Earnings',
  employeeTaxes: 'Employee taxes',
  preTax: 'Pre-tax deductions',
  postTax: 'Post-tax deductions',
  employerPaid: 'Employer paid',
  taxableWages: 'Taxable wages',
};

export const PayrollRecordModal = ({record, onRequestClose}: Props) => {
  const lines = React.useMemo(() => toSlipLines(record.lines), [record.lines]);
  const sections = React.useMemo(() => {
    const bySection = new Map<string, SlipLine[]>();
    for (const line of lines) {
      const existing = bySection.get(line.section);
      if (existing) {
        existing.push(line);
      } else {
        bySection.set(line.section, [line]);
      }
    }
    return [...bySection.entries()];
  }, [lines]);

  const summaryRows: Array<[string, number]> = [
    ['Gross', record.gross],
    ['Taxes withheld', -record.taxesWithheld],
    ['Other pre-tax', -record.otherPretax],
    ['Net deposited', record.net],
  ];

  return (
    <Modal
      onRequestClose={onRequestClose}
      header={
        <div>
          <h2 className="text-lg font-semibold">{record.slipId}</h2>
          <p className="text-muted-foreground text-sm">
            {record.employer} ·{' '}
            {fromSeconds(record.payDate).toLocaleString({
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
          {summaryRows.map(([label, value]) => (
            <div key={label}>
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="font-medium tabular-nums">{cents(value)}</dd>
            </div>
          ))}
        </dl>

        {sections.map(([section, sectionLines]) => (
          <div key={section}>
            <h3 className="text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wide">
              {SECTION_LABELS[section] ?? section}
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground text-left text-xs">
                  <th className="py-0.5 font-normal">Description</th>
                  <th className="py-0.5 text-right font-normal">Amount</th>
                  <th className="py-0.5 text-right font-normal">YTD</th>
                </tr>
              </thead>
              <tbody>
                {sectionLines.map((line, index) => (
                  <tr
                    key={`${line.description}-${index}`}
                    className="border-border border-t"
                  >
                    <td className="py-1 pr-2">{line.description}</td>
                    <td className="py-1 text-right tabular-nums">
                      {dollars(line.amount)}
                    </td>
                    <td className="text-muted-foreground py-1 text-right tabular-nums">
                      {line.ytd != null ? dollars(line.ytd) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {lines.length === 0 && (
          <p className="text-muted-foreground text-sm">
            No line items were stored for this record.
          </p>
        )}
      </div>
    </Modal>
  );
};
